import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Coach-side modal for wiring up WhatsApp Business API.
 *
 * What the coach needs (from Meta Business Manager):
 *   - phone_number_id  — the WABA phone's internal ID
 *   - access_token     — a long-lived system-user token with
 *                         whatsapp_business_messaging permission
 *
 * The token is encrypted at rest (Fernet) on the backend. The public
 * GET /config endpoint only returns the phone_number_id +
 * business_display_name; the token is never echoed back.
 *
 * Future: replace this raw-credential form with a Meta Embedded
 * Signup flow so coaches don't see tokens at all.
 */
export default function WhatsAppConfig({ onClose, theme = 'dark' }) {
  const { token } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [phoneId, setPhoneId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/coach/whatsapp/config`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      setCfg(r.data);
      setPhoneId(r.data?.phone_number_id || '');
      setBusinessName(r.data?.business_display_name || '');
    }).catch(() => setCfg({ configured: false }));

    axios.get(`${API}/coach/whatsapp/roster-preview`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setRecipientPreview(r.data)).catch(() => {});
  }, [token]);

  const save = async () => {
    if (!phoneId.trim()) {
      toast.error('Phone number ID is required');
      return;
    }
    if (!accessToken.trim() && !cfg?.configured) {
      toast.error('Access token is required on first save');
      return;
    }
    setBusy(true);
    try {
      const body = {
        phone_number_id: phoneId.trim(),
        business_display_name: businessName.trim() || undefined,
      };
      if (accessToken.trim()) body.access_token = accessToken.trim();
      const r = await axios.post(`${API}/coach/whatsapp/config`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCfg(r.data);
      setAccessToken('');
      toast.success('WhatsApp connected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove WhatsApp config? You can re-add it later.')) return;
    try {
      await axios.delete(`${API}/coach/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCfg({ configured: false });
      setPhoneId(''); setAccessToken(''); setBusinessName('');
      toast.success('Disconnected');
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl p-5 max-w-md w-full"
        style={{
          background: '#0a0e27', color: '#f7fafc',
          border: '1px solid rgba(0,201,167,0.3)',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold"
                 style={{ color: '#25D366' }}>
              WhatsApp Business
            </div>
            <h2 className="text-lg font-bold mt-0.5">
              {cfg?.configured ? 'Connected' : 'Connect WhatsApp'}
            </h2>
          </div>
          <button onClick={onClose} className="text-xl opacity-60 hover:opacity-100">✕</button>
        </div>

        <p className="text-xs opacity-70 mb-3">
          Connect your Meta-verified WhatsApp Business number so you can
          blast match cards to your whole roster in one tap.
        </p>

        <label className="block text-xs uppercase tracking-widest font-bold opacity-70 mb-1.5">
          Phone Number ID
        </label>
        <input
          value={phoneId}
          onChange={(e) => setPhoneId(e.target.value)}
          placeholder="e.g. 109876543210987"
          className="w-full px-3 py-2 rounded-lg outline-none text-sm font-mono"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        />

        <label className="block text-xs uppercase tracking-widest font-bold opacity-70 mb-1.5 mt-3">
          Access Token{cfg?.configured ? ' (leave blank to keep current)' : ''}
        </label>
        <input
          value={accessToken}
          type="password"
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder="EAAG..."
          className="w-full px-3 py-2 rounded-lg outline-none text-sm font-mono"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        />

        <label className="block text-xs uppercase tracking-widest font-bold opacity-70 mb-1.5 mt-3">
          Business display name (optional)
        </label>
        <input
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Coach Vinay U13s"
          className="w-full px-3 py-2 rounded-lg outline-none text-sm"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        />

        {recipientPreview && (
          <div
            className="mt-4 px-3 py-2.5 rounded-lg text-xs"
            style={{ background: 'rgba(0,201,167,0.08)',
                     border: '1px solid rgba(0,201,167,0.2)' }}
          >
            <div className="font-semibold mb-1">
              {recipientPreview.count} parent{recipientPreview.count === 1 ? '' : 's'} on your roster
            </div>
            <div className="opacity-70">
              Each blast goes to every parent who's accepted your invite.
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-5">
          {cfg?.configured && (
            <button
              onClick={remove}
              className="px-3 py-2 rounded-lg text-sm font-semibold"
              style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}
            >
              Disconnect
            </button>
          )}
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          >
            {busy ? 'Saving…' : (cfg?.configured ? 'Update' : 'Connect')}
          </button>
        </div>

        <p className="text-[10px] opacity-50 mt-3">
          We never see your access token in plaintext — it's encrypted on
          our server with a Fernet key the database doesn't hold.
        </p>
      </div>
    </div>
  );
}
